import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, ArrowDownLeft, ArrowUpRight, IndianRupee } from 'lucide-react';
import { toast } from 'sonner';
import { formatPrice } from '@/lib/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ label, value, subLabel, growth, prefix = '₹' }) => {
  const isPositive = growth >= 0;
  return (
    <div className="bg-white border border-stone-200 rounded-lg p-4">
      <div className="text-xs text-stone-500 font-medium mb-1">{label}</div>
      <div className="text-xl font-bold text-stone-900 tabular-nums">{prefix}{formatPrice(value)}</div>
      {subLabel && <div className="text-[10px] text-stone-400 mt-0.5">{subLabel}</div>}
      {growth !== undefined && growth !== null && (
        <div className={`flex items-center gap-1 mt-1 text-xs font-semibold ${isPositive ? 'text-lime-700' : 'text-red-700'}`}>
          {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {isPositive ? '+' : ''}{growth}% vs prev
        </div>
      )}
    </div>
  );
};

const FinancialDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [receivables, setReceivables] = useState([]);
  const [payables, setPayables] = useState([]);
  const [adjustments, setAdjustments] = useState(null);
  const [adjPeriod, setAdjPeriod] = useState('this_month');
  const [loading, setLoading] = useState(true);
  const [profitView, setProfitView] = useState('project');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [sumRes, recRes, payRes, adjRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/financial/summary`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/financial/receivables`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/financial/payables`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/financial/adjustments`, { withCredentials: true })
      ]);
      setSummary(sumRes.data);
      setReceivables(recRes.data);
      setPayables(payRes.data);
      setAdjustments(adjRes.data);
    } catch (error) {
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-stone-600">Loading financial data...</div>;
  if (!summary) return <div className="text-stone-600">No data available</div>;

  const totalReceivable = receivables.reduce((s, r) => s + r.outstanding, 0);
  const totalPayable = payables.reduce((s, p) => s + p.outstanding, 0);

  // Simple bar chart renderer
  const BarChart = ({ data, maxVal }) => {
    if (!data.length) return <div className="text-xs text-stone-400 text-center py-4">No data yet</div>;
    const max = maxVal || Math.max(...data.map(d => Math.max(d.revenue, Math.abs(d.net_profit))), 1);
    return (
      <div className="space-y-1.5">
        {data.map((d) => (
          <div key={d.month} className="flex items-center gap-2 text-xs">
            <span className="w-16 text-stone-500 tabular-nums text-right shrink-0">{d.month}</span>
            <div className="flex-1 flex gap-1 items-center">
              <div className="h-4 rounded-sm bg-blue-400" style={{ width: `${Math.max((d.revenue / max) * 100, 1)}%` }} title={`Sales: ₹${formatPrice(d.revenue)}`}></div>
              <div className={`h-4 rounded-sm ${d.net_profit >= 0 ? 'bg-lime-500' : 'bg-red-400'}`} style={{ width: `${Math.max((Math.abs(d.net_profit) / max) * 100, 1)}%` }} title={`Net Profit: ₹${formatPrice(d.net_profit)}`}></div>
            </div>
            <span className="w-20 text-right tabular-nums text-stone-700 shrink-0">₹{formatPrice(d.revenue)}</span>
          </div>
        ))}
        <div className="flex items-center gap-3 text-[10px] text-stone-500 mt-2 ml-16">
          <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm bg-blue-400 inline-block"></span> Sales</span>
          <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm bg-lime-500 inline-block"></span> Net Profit</span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Financial Dashboard</h1>
        <p className="mt-1 text-xs text-stone-600">Sales, profit, receivables & payables at a glance</p>
      </div>

      {/* Sales Row */}
      <div>
        <h2 className="text-sm font-semibold text-stone-700 mb-2">Sales</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <StatCard label="This Month" value={summary.this_month.revenue} growth={summary.month_growth} subLabel={`Cost: ₹${formatPrice(summary.this_month.cost)}`} />
          <StatCard label="This Quarter" value={summary.this_quarter.revenue} growth={summary.quarter_growth} subLabel={`Cost: ₹${formatPrice(summary.this_quarter.cost)}`} />
          <StatCard label="This Year" value={summary.this_year.revenue} growth={summary.year_growth} subLabel={`Cost: ₹${formatPrice(summary.this_year.cost)}`} />
        </div>
      </div>

      {/* Profit Row */}
      <div>
        <h2 className="text-sm font-semibold text-stone-700 mb-2">Net Profit <span className="text-stone-400 font-normal">(Sales - Vendor Costs - Expenses ± Settlements)</span></h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <StatCard label="This Month" value={summary.this_month.net_profit} subLabel={`Expenses: ₹${formatPrice(summary.this_month.expenses)}${summary.this_month.revenue_adjustments ? ` · Discount given: ₹${formatPrice(summary.this_month.revenue_adjustments)}` : ''}${summary.this_month.cost_adjustments ? ` · Vendor savings: ₹${formatPrice(summary.this_month.cost_adjustments)}` : ''}`} />
          <StatCard label="This Quarter" value={summary.this_quarter.net_profit} subLabel={`Expenses: ₹${formatPrice(summary.this_quarter.expenses)}${summary.this_quarter.revenue_adjustments ? ` · Discount given: ₹${formatPrice(summary.this_quarter.revenue_adjustments)}` : ''}${summary.this_quarter.cost_adjustments ? ` · Vendor savings: ₹${formatPrice(summary.this_quarter.cost_adjustments)}` : ''}`} />
          <StatCard label="This Year" value={summary.this_year.net_profit} subLabel={`Expenses: ₹${formatPrice(summary.this_year.expenses)}${summary.this_year.revenue_adjustments ? ` · Discount given: ₹${formatPrice(summary.this_year.revenue_adjustments)}` : ''}${summary.this_year.cost_adjustments ? ` · Vendor savings: ₹${formatPrice(summary.this_year.cost_adjustments)}` : ''}`} />
        </div>
      </div>

      {/* Receivables & Payables Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white border border-stone-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <ArrowDownLeft className="w-4 h-4 text-lime-700" />
            <span className="text-xs font-semibold text-stone-700">To Collect from Clients</span>
          </div>
          <div className="text-xl font-bold text-lime-800 tabular-nums" data-testid="total-receivable">₹{formatPrice(totalReceivable)}</div>
          <div className="text-[10px] text-stone-400">{receivables.filter(r => r.outstanding > 0).length} clients with pending dues</div>
        </div>
        <div className="bg-white border border-stone-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <ArrowUpRight className="w-4 h-4 text-red-700" />
            <span className="text-xs font-semibold text-stone-700">To Pay to Vendors</span>
          </div>
          <div className="text-xl font-bold text-red-800 tabular-nums" data-testid="total-payable">₹{formatPrice(totalPayable)}</div>
          <div className="text-[10px] text-stone-400">{payables.filter(p => p.outstanding > 0).length} vendors with pending dues</div>
        </div>
      </div>

      {/* Monthly Trend */}
      <div className="bg-white border border-stone-200 rounded-lg p-4">
        <h2 className="text-sm font-semibold text-stone-700 mb-3">Monthly Trend</h2>
        <BarChart data={summary.monthly_data.slice(-12)} />
      </div>

      {/* Profit by Project / Customer Toggle */}
      <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
        <div className="flex items-center gap-1 p-3 border-b border-stone-200 bg-stone-50">
          <button
            data-testid="profit-by-project-tab"
            onClick={() => setProfitView('project')}
            className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${profitView === 'project' ? 'bg-white shadow-sm text-stone-900' : 'text-stone-500 hover:text-stone-700'}`}
          >By Project</button>
          <button
            data-testid="profit-by-customer-tab"
            onClick={() => setProfitView('customer')}
            className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${profitView === 'customer' ? 'bg-white shadow-sm text-stone-900' : 'text-stone-500 hover:text-stone-700'}`}
          >By Customer</button>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-stone-50">
            <tr className="border-b border-stone-200">
              <th className="text-left p-2.5 font-semibold text-stone-600">{profitView === 'project' ? 'PROJECT' : 'CUSTOMER'}</th>
              {profitView === 'project' && <th className="text-left p-2.5 font-semibold text-stone-600">CUSTOMER</th>}
              <th className="text-right p-2.5 font-semibold text-stone-600">REVENUE</th>
              <th className="text-right p-2.5 font-semibold text-stone-600">COST</th>
              <th className="text-right p-2.5 font-semibold text-stone-600">GROSS PROFIT</th>
              <th className="text-right p-2.5 font-semibold text-stone-600">MARGIN</th>
            </tr>
          </thead>
          <tbody>
            {(profitView === 'project' ? summary.by_project : summary.by_customer).length === 0 ? (
              <tr><td colSpan={profitView === 'project' ? 6 : 5} className="p-6 text-center text-stone-500">No delivered orders yet</td></tr>
            ) : (
              (profitView === 'project' ? summary.by_project : summary.by_customer).map((item, idx) => {
                const margin = item.revenue > 0 ? ((item.profit / item.revenue) * 100).toFixed(1) : '0.0';
                return (
                  <tr key={item.project_id || item.customer_id} className={`border-b border-stone-100 hover:bg-stone-50 ${idx % 2 === 0 ? '' : 'bg-stone-50/50'}`}>
                    <td className="p-2.5 font-medium text-stone-900">{item.project_name || item.customer_name}</td>
                    {profitView === 'project' && <td className="p-2.5 text-stone-600">{item.customer_name}</td>}
                    <td className="p-2.5 text-right tabular-nums text-stone-700">₹{formatPrice(item.revenue)}</td>
                    <td className="p-2.5 text-right tabular-nums text-stone-500">₹{formatPrice(item.cost)}</td>
                    <td className="p-2.5 text-right tabular-nums font-semibold">
                      <span className={item.profit >= 0 ? 'text-lime-700' : 'text-red-700'}>₹{formatPrice(item.profit)}</span>
                    </td>
                    <td className="p-2.5 text-right tabular-nums">
                      <span className={parseFloat(margin) >= 0 ? 'text-lime-700' : 'text-red-700'}>{margin}%</span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Adjustments Report */}
      {adjustments && (
        <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
          <div className="p-3 border-b border-stone-200 bg-stone-50 flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-xs font-semibold text-stone-700">Settlement Adjustments</h3>
              <p className="text-[10px] text-stone-500">Money written off via Discount or absorbed via Round-off — never received as cash.</p>
            </div>
            <div className="flex gap-1 bg-stone-100 p-0.5 rounded-md">
              {[
                { key: 'this_month', label: 'Month' },
                { key: 'this_quarter', label: 'Quarter' },
                { key: 'this_year', label: 'Year' },
                { key: 'all_time', label: 'All-time' },
              ].map(p => (
                <button
                  key={p.key}
                  data-testid={`adj-period-${p.key}`}
                  onClick={() => setAdjPeriod(p.key)}
                  className={`px-2.5 py-1 rounded text-[11px] font-semibold transition-colors ${adjPeriod === p.key ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          {(() => {
            const a = adjustments[adjPeriod] || {};
            const ci = a.cash_in || { discount: 0, round_off: 0 };
            const co = a.cash_out || { discount: 0, round_off: 0 };
            const ciTotal = a.cash_in_total || 0;
            const coTotal = a.cash_out_total || 0;
            const net = a.net_writeoff || 0;
            return (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x divide-stone-200">
                <div className="p-4">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-2">Customer-side (Cash-In)</div>
                  <div className="flex items-baseline justify-between text-xs mb-1">
                    <span className="text-stone-600">Discount given</span>
                    <span className="tabular-nums font-semibold text-amber-700" data-testid="adj-ci-discount">₹{formatPrice(ci.discount)}</span>
                  </div>
                  <div className="flex items-baseline justify-between text-xs">
                    <span className="text-stone-600">Round-off</span>
                    <span className={`tabular-nums font-semibold ${ci.round_off >= 0 ? 'text-stone-600' : 'text-red-700'}`} data-testid="adj-ci-roundoff">₹{formatPrice(ci.round_off)}</span>
                  </div>
                  <div className="border-t border-stone-200 mt-2 pt-2 flex items-baseline justify-between text-xs">
                    <span className="font-semibold text-stone-700">Total written off</span>
                    <span className="tabular-nums font-bold text-red-700" data-testid="adj-ci-total">₹{formatPrice(ciTotal)}</span>
                  </div>
                </div>
                <div className="p-4">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-2">Vendor-side (Cash-Out)</div>
                  <div className="flex items-baseline justify-between text-xs mb-1">
                    <span className="text-stone-600">Discount received</span>
                    <span className="tabular-nums font-semibold text-lime-700" data-testid="adj-co-discount">₹{formatPrice(co.discount)}</span>
                  </div>
                  <div className="flex items-baseline justify-between text-xs">
                    <span className="text-stone-600">Round-off</span>
                    <span className={`tabular-nums font-semibold ${co.round_off >= 0 ? 'text-stone-600' : 'text-lime-700'}`} data-testid="adj-co-roundoff">₹{formatPrice(co.round_off)}</span>
                  </div>
                  <div className="border-t border-stone-200 mt-2 pt-2 flex items-baseline justify-between text-xs">
                    <span className="font-semibold text-stone-700">Total saved</span>
                    <span className="tabular-nums font-bold text-lime-700" data-testid="adj-co-total">₹{formatPrice(coTotal)}</span>
                  </div>
                </div>
                <div className="p-4 bg-stone-50/50">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-2">Net Impact</div>
                  <div className={`text-2xl font-bold tabular-nums ${net > 0 ? 'text-red-700' : 'text-lime-700'}`} data-testid="adj-net">
                    {net > 0 ? '-' : net < 0 ? '+' : ''}₹{formatPrice(Math.abs(net))}
                  </div>
                  <p className="text-[10px] text-stone-500 mt-1.5 leading-relaxed">
                    {net > 0
                      ? 'You wrote off more than you saved this period.'
                      : net < 0
                      ? 'Vendor savings exceed customer write-offs. Net positive.'
                      : 'No settlement adjustments yet.'}
                  </p>
                </div>
              </div>
            );
          })()}
          {adjustments.recent && adjustments.recent.length > 0 && (
            <div className="border-t border-stone-200">
              <div className="px-4 py-2 text-[10px] uppercase tracking-wider font-semibold text-stone-500 bg-stone-50">Recent adjustments</div>
              <table className="w-full text-xs">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="text-left p-2 font-semibold text-stone-600">DATE</th>
                    <th className="text-left p-2 font-semibold text-stone-600">SIDE</th>
                    <th className="text-left p-2 font-semibold text-stone-600">TYPE</th>
                    <th className="text-right p-2 font-semibold text-stone-600">AMOUNT</th>
                    <th className="text-left p-2 font-semibold text-stone-600">LINKED PAYMENT</th>
                  </tr>
                </thead>
                <tbody>
                  {adjustments.recent.map((r) => (
                    <tr key={r.payment_id} data-testid={`adj-recent-${r.payment_id}`} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="p-2 tabular-nums text-stone-700">{r.date}</td>
                      <td className="p-2">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${r.type === 'cash_in' ? 'bg-amber-100 text-amber-800' : 'bg-lime-100 text-lime-800'}`}>
                          {r.type === 'cash_in' ? 'Customer' : 'Vendor'}
                        </span>
                      </td>
                      <td className="p-2 text-stone-700 capitalize">{r.adjustment_type?.replace('_', '-')}</td>
                      <td className="p-2 text-right tabular-nums font-semibold text-stone-900">₹{formatPrice(r.amount)}</td>
                      <td className="p-2 text-stone-500 font-mono text-[10px]">{r.linked_payment_id || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FinancialDashboard;
